<template>
  <div>
    <div class="grid grid-4" v-if="files.length > 0">
      <div class="column-title"></div>
      <div class="column-title">{{ $t("files.name") }}</div>
      <div class="column-title">{{ $t("files.date") }}</div>
      <div class="column-title"></div>
    </div>
    <template v-for="file in files">
      <div class="grid row grid-4">
        <div class="icon-column">
          <svg width="15px" height="16px" class="icon" v-if="!showFileStatus || file.read">
            <use xlink:href="#file"></use>
          </svg>
          <svg width="18px" height="20px" class="icon" v-if="showFileStatus && !file.read">
            <use xlink:href="#new-file"></use>
          </svg>
        </div>
        <div class="column">
          <div class="default-text">{{file.displayName}}</div>
        </div>
        <div class="column">
          <div class="default-text">{{ $d(file.updateDate, 'short') }}</div>
        </div>
        <div class="actions">
          <button class="ghost-button blue-hoover" type="button"
                  v-on:click="downloadFile(file)" v-if="canDownload">
            {{$t("actions.download")}}
          </button>
          <button class="ghost-button blue-hoover" type="button"
                  v-on:click="showRenameModal(file)" v-if="canRename">
            {{$t("actions.rename")}}
          </button>
          <button class="ghost-button red-hoover" type="button"
                  v-on:click="showDeleteModal(file)" v-if="canDelete">
            {{$t("actions.delete")}}
          </button>
        </div>
      </div>
    </template>
    <simple-modal
      v-if="canDelete"
      ref="deleteModal"
      :model="selectedFile"
      v-on:confirm="deleteFile(selectedFile.id)"
      v-show="isDeleteModalVisible"
      @cancel="closeDeleteModal">
      <!--use #header instead of https://vuejs.org/v2/guide/components-slots.html#Deprecated-Syntax-->
      <template #header>
        <p class="modal-title">{{ $t("files.delete_modal_title") }}</p>
      </template>
      <template #body="{model}">
        <div class="modal-layout">
          <p class="modal-content"
             v-html="$t('files.delete_modal_content', {name: model.displayName})"></p>
        </div>
      </template>
    </simple-modal>
    <simple-modal
      v-if="canRename"
      ref="renameModal"
      :model="selectedFile"
      :action-label="$t('actions.rename')"
      v-on:confirm="renameFile(selectedFile, newFilename)"
      v-show="isRenameModalVisible"
      @cancel="closeRenameModal">
      <!--use #header instead of https://vuejs.org/v2/guide/components-slots.html#Deprecated-Syntax-->
      <template #header>
        <p class="modal-title">{{ $t("files.rename_modal_title") }}</p>
      </template>
      <template #body="{model}">
        <div class="flex-modal-layout">
          <c-input ref="filename" name="filename" label="files.rename_modal_content" :maxLength="50" v-model.trim="newFilename"
                   type="text" v-validate="'required'"/>
          <p>.{{model.extension}}</p>
        </div>
      </template>
    </simple-modal>
  </div>
</template>

<script>
  import simpleModal from "../components/simpleModal.vue";
  import fileService from "../services/fileService.js";
  import Utils from "../services/utils.js";
  import File from "../models/File.js";

  export default {
    name: "fileList",
    props: {
      showFileStatus: Boolean,
      canDownload: Boolean,
      canRename: Boolean,
      canDelete: Boolean,
      clientId: {
        type: Number,
        default: function () {
          return null
        }
      },
      files: {
        type: Array,
        default: function () {
          return []
        }
      }
    },
    components: {
      simpleModal
    },
    data() {
      return {
        newFilename: "",
        selectedFile: {},
        isDeleteModalVisible: false,
        isRenameModalVisible: false
      };
    },
    methods: {
      downloadFile(file) {
        const displayName = file.displayName;

        fileService.downloadFile(file.id, this.clientId)
          .then(response => {
            file.read = true;
            this.$store.dispatch('file/count');
          })
          .catch(error => {
            this.$store.dispatch('alert/error', {
              message: this.$t("alerts.download_file_error", {name: displayName}),
              error: error
            });
          });
      },
      renameFile(file, newFilename) {
        if (Utils.isBlank(newFilename)) {
          this.$refs.filename.errors.add({
            field: 'filename',
            msg: this.$t('messages.filename')
          });
          return;
        }
        const fileCopy = new File(file); // avoid modifying the list, wait for reload if success
        fileCopy.name = newFilename;

        const oldName = file.displayName;
        const newName = fileCopy.displayName;

        fileService.renameFile(fileCopy, this.clientId)
          .then(response => {
            this.$store.dispatch('alert/success', this.$t("alerts.rename_file_success", {
              oldName: oldName,
              newName: newName
            }));
            this.$emit('refresh')
          })
          .catch(error => {
            this.$store.dispatch('alert/error', {
              message: this.$t("alerts.rename_file_error", {oldName: oldName, newName: newName}),
              error: error
            });
          });
        this.closeRenameModal();
      },
      deleteFile(fileId) {
        const displayName = this.selectedFile.displayName;

        fileService.deleteFile(fileId, this.clientId)
          .then(response => {
            this.$store.dispatch('alert/success', this.$t("alerts.delete_file_success", {name: displayName}));
            this.$store.dispatch('file/count');
            this.$emit('refresh')
          })
          .catch(error => {
            this.$store.dispatch('alert/error', {
              message: this.$t("alerts.delete_file_error", {name: displayName}),
              error: error
            });
          });
        this.closeDeleteModal();
      },
      showDeleteModal(file) {
        this.selectedFile = file;
        this.isDeleteModalVisible = true;
      },
      closeDeleteModal() {
        this.selectedFile = {};
        this.isDeleteModalVisible = false;
      },
      showRenameModal(file) {
        this.selectedFile = file;
        this.newFilename = file.name;
        this.isRenameModalVisible = true;
      },
      closeRenameModal() {
        this.selectedFile = {};
        this.$refs.filename.errors.clear();
        this.isRenameModalVisible = false;
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../style/list.scss';

  .grid-4 {
    grid-template-columns: 30px 1fr 1fr 300px;
  }

  .icon {
    margin-right: 5px;
    color: $bluish-grey;
  }

  .icon-column {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
  }

  .flex-modal-layout {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: stretch;
    align-items: flex-start;

    width: 400px;

    .field {
      flex-grow: 4
    }

    p {
      @extend %font-default;
      flex-grow: 1;
      margin-top: 39px;
      margin-bottom: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/fileList.vue